import React, {
  createContext, useContext, useMemo, useState, useEffect,
} from 'react';
import { graphql, useStaticQuery } from 'gatsby';

const getFieldValue = (field) => {
  if (field && typeof field === 'object' && 'value' in field) {
    return field.value;
  }

  return field;
};

const isFrontsiteEnvironment = (environment) => {
  const displayValue = String(environment?.display || '').trim().toLowerCase();
  return displayValue === 'frontsite';
};

const getEnvironmentItems = (environmentsField) => {
  const rawValue = getFieldValue(environmentsField);

  if (Array.isArray(rawValue)) {
    return rawValue;
  }

  if (Array.isArray(environmentsField)) {
    return environmentsField;
  }

  return [];
};

const normalizeCurrency = (currency) => ({
  id: String(currency.id || `${getFieldValue(currency.Code)}-${getFieldValue(currency.Name)}`),
  code: String(getFieldValue(currency.Code) || 'EUR'),
  name: String(getFieldValue(currency.Name) || getFieldValue(currency.Code) || 'EURO'),
  exchangeRate: Number(getFieldValue(currency.Exchange_rate)) || 1,
  decimals: Number.isFinite(Number(getFieldValue(currency.Decimals)))
    ? Number(getFieldValue(currency.Decimals))
    : 2,
  primary: Boolean(getFieldValue(currency.Primary)),
});

// Maps browser locale to a currency code. Returns null if no match.
const LOCALE_CURRENCY_RULES = [
  { test: (l) => l.startsWith('cs') || l.startsWith('cz'), code: 'CZK' },
  { test: (l) => l.startsWith('sk'), code: 'EUR' },
  { test: (l) => l === 'en-us', code: 'USD' },
];

const detectCurrencyFromLocale = (availableCodes) => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return null;
  const lang = (navigator.language || '').toLowerCase();
  const match = LOCALE_CURRENCY_RULES.find(({ test }) => test(lang));
  const code = match?.code ?? null;
  return code && availableCodes.includes(code) ? code : null;
};

const fallbackCurrencies = [
  {
    id: 'fallback-eur',
    code: 'EUR',
    name: 'EURO',
    exchangeRate: 1,
    decimals: 2,
    primary: true,
  },
];

const fallbackDefaultCurrency = fallbackCurrencies[0];

const CurrencyContext = createContext({
  currencies: fallbackCurrencies,
  selectedCurrency: fallbackDefaultCurrency,
  setSelectedCurrencyByCode: () => {},
  convertFromBaseEur: (value) => value,
  formatCurrencyAmount: (value) => `${value}`,
});

export const CurrencyProvider = ({ children }) => {
  const data = useStaticQuery(graphql`
    query CurrencyProviderQuery {
      allCockpitCurrency {
        edges {
          node {
            id
            Code {
              value
            }
            Name {
              value
            }
            Exchange_rate {
              value
            }
            Decimals {
              value
            }
            Primary {
              value
            }
            Enabled {
              value
            }
            Environments {
              value {
                display
              }
            }
          }
        }
      }
    }
  `);

  const availableCurrencies = useMemo(() => {
    const nodes = (data?.allCockpitCurrency?.edges || []).map((edge) => edge.node);

    const normalized = nodes
      .filter((currency) => {
        const enabledValue = getFieldValue(currency.Enabled);
        return enabledValue === true
          || enabledValue === 'true'
          || enabledValue === 1
          || enabledValue === '1';
      })
      .filter((currency) => getEnvironmentItems(currency.Environments).some(isFrontsiteEnvironment))
      .map(normalizeCurrency);

    // Cockpit data can be duplicated per language. Keep first entry for each code.
    const byCode = new Map();
    normalized.forEach((currency) => {
      if (!byCode.has(currency.code)) {
        byCode.set(currency.code, currency);
      }
    });

    return Array.from(byCode.values());
  }, [data]);

  const currencies = availableCurrencies.length > 0 ? availableCurrencies : fallbackCurrencies;
  const defaultCurrency = currencies.find((currency) => currency.primary) || currencies[0];

  const [selectedCode, setSelectedCode] = useState(defaultCurrency.code);

  useEffect(() => {
    if (!currencies.some((currency) => currency.code === selectedCode)) {
      setSelectedCode(defaultCurrency.code);
    }
  }, [currencies, defaultCurrency, selectedCode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedCode = window.localStorage.getItem('hostsoft-currency');
    if (storedCode && currencies.some((currency) => currency.code === storedCode)) {
      setSelectedCode(storedCode);
      return;
    }

    // First visit — auto-detect from browser locale
    const availableCodes = currencies.map((c) => c.code);
    const detected = detectCurrencyFromLocale(availableCodes);
    if (detected) {
      setSelectedCode(detected);
      window.localStorage.setItem('hostsoft-currency', detected);
    }
  }, [currencies]);

  const setSelectedCurrencyByCode = (code) => {
    setSelectedCode(code);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('hostsoft-currency', code);
    }
  };

  const selectedCurrency = useMemo(
    () => currencies.find((currency) => currency.code === selectedCode) || defaultCurrency,
    [selectedCode, currencies, defaultCurrency],
  );

  const convertFromBaseEur = (baseEurValue) => {
    const numericValue = Number(baseEurValue) || 0;
    return numericValue * selectedCurrency.exchangeRate;
  };

  const formatCurrencyAmount = (value) => {
    const numericValue = Number(value) || 0;
    return numericValue.toFixed(selectedCurrency.decimals);
  };

  const contextValue = {
    currencies,
    selectedCurrency,
    setSelectedCurrencyByCode,
    convertFromBaseEur,
    formatCurrencyAmount,
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
